import { Paths } from "@constants/paths";
import { APP_VERSION_FORMAT } from "@constants/ui";
import useUserMode from "@hooks/useUserMode";
import { RootState } from "@stores";
import logger from "@utils/logger";
import { PropsWithClassName, cn } from "kz-ui-sdk";
import { truncate } from "lodash-es";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { matchPath, useLocation } from "react-router-dom";

interface AppVersionProps extends PropsWithClassName {
  format?: string;
  versionFileUrl?: string;
}

const API_URLS: Record<string, string[]> = {
  ["STG - PH1"]: ["https://api.tph1.trykz.dev"],
  ["STG - TH1"]: ["https://api.tth1.trykz.dev"],
  ["STG - PG1"]: ["https://api.tpg1.trykz.dev"],
  ["STG - TG1"]: ["https://api.ttg1.trykz.dev"],
  ["PRD - PH1"]: ["https://api.phkz1api.com"],
};

const AppVersion = ({ format = APP_VERSION_FORMAT, className }: AppVersionProps) => {
  const [versionNumber, setVersionNumber] = useState<string | undefined>();
  const [serverName, setServerName] = useState<string>();
  const isWsConnected = useSelector((state: RootState) => state?.preferences?.isWsConnected);
  const { isMember } = useUserMode();
  const location = useLocation();

  const getVersionNumber = useCallback(() => {
    try {
      const version = import.meta.env.VITE_APP_VERSION || moment().format(format);
      setVersionNumber(version);
    } catch (error) {
      logger._console.log("Running in development mode");
    }
  }, [format]);

  useEffect(() => {
    getVersionNumber();
  }, [getVersionNumber]);

  useEffect(() => {
    // Only display server name in DEV environment
    if (import.meta.env.DEV) {
      const baseAPI = import.meta.env.VITE_API_BASE_URI;
      const serverName = Object.keys(API_URLS).find((key) => API_URLS[key].includes(baseAPI));
      setServerName(serverName);
    }
  }, []);

  const isShowWSIndicator = useMemo(() => {
    return isMember && !matchPath(Paths.PRIVATE.DEPOSIT_CALLBACK, location.pathname);
  }, [isMember, location.pathname]);

  const displayVersion = useMemo(() => {
    if (versionNumber) {
      return `v${truncate(versionNumber, { length: 8, omission: "" })}`;
    }
    return `v${moment().format(format)}`;
  }, [versionNumber, format]);

  return (
    <div className="-translate-start-1/2 pointer-events-none fixed bottom-0 start-1/2 z-[9999] w-full max-w-md">
      <span
        className={cn(
          "origin-bottom-end absolute bottom-1 end-2.5 rotate-90 text-[7px] text-content-soft [text-shadow:0.25px_0.25px_0px_#000] rtl:-rotate-90",
          className,
        )}
        title={versionNumber}
      >
        {isShowWSIndicator && (
          <span
            className={cn(
              "absolute -start-1.5 bottom-1 flex size-1 rounded-full",
              isWsConnected ? "bg-success-base" : "bg-content-soft",
            )}
          />
        )}
        {displayVersion}
      </span>
      {serverName && (
        <span
          className={cn(
            "origin-bottom-start absolute bottom-1 start-3 -rotate-90 text-[7px] text-content-soft [text-shadow:0.25px_0.25px_0px_#000] rtl:rotate-90",
            {
              "text-yellow-400": serverName.startsWith("STG"),
              "text-red-400": serverName.startsWith("PRD"),
            },
            className,
          )}
        >
          {serverName}
        </span>
      )}
    </div>
  );
};

export default AppVersion;
